/**
 * Catalogue centralisé des articles de blog Mediastore Niort.
 *
 * Chaque article :
 *   slug          : identifiant URL (kebab-case, sans accents)
 *   title         : titre H1 + meta (max 65 car. recommandé)
 *   description   : meta description (max 160 car.)
 *   keywords      : mots-clés cibles SEO
 *   date          : ISO yyyy-mm-dd (utilisé en JSON-LD datePublished)
 *   author        : auteur (Mediastore)
 *   category      : Réparation / Conseils / Reconditionné / Pro / Actu
 *   readTime      : minutes
 *   image         : illustration en tête (URL hébergée Emergent)
 *   excerpt       : résumé visible sur la liste d'articles (≈ 180 car.)
 *   sections      : tableau de blocs { type: 'h2'|'h3'|'p'|'ul'|'ol'|'note'|'cta', content }
 *
 * Ajoutez de nouveaux articles en complétant ce fichier.
 */

const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg";

export const BLOG_ARTICLES = [
  {
    slug: "changer-batterie-iphone-niort",
    title: "Changer la batterie de son iPhone à Niort : guide complet 2026",
    description:
      "Quand changer la batterie iPhone ? Quels signes surveiller ? Combien ça coûte chez Mediastore Niort ? Guide complet et tarifs.",
    keywords:
      "batterie iphone niort, changer batterie iphone, santé batterie iphone, autonomie iphone, mediastore batterie",
    date: "2026-02-01",
    author: "Mediastore",
    category: "Réparation",
    readTime: 6,
    image: DEFAULT_IMAGE,
    excerpt:
      "Votre iPhone se décharge en quelques heures ? La santé batterie chute sous 80 % ? Voici tout ce qu'il faut savoir avant de changer la batterie, et nos tarifs pour Niort.",
    sections: [
      {
        type: "p",
        content:
          "La batterie d'un iPhone se dégrade naturellement avec le temps. Apple indique qu'une batterie conserve environ 80 % de sa capacité d'origine après 500 cycles complets de charge, ce qui correspond à 2 à 3 ans d'utilisation normale. Au-delà, plusieurs signes doivent vous alerter et envisager un remplacement chez un spécialiste comme Mediastore Niort.",
      },
      { type: "h2", content: "Comment savoir si la batterie iPhone doit être remplacée ?" },
      {
        type: "p",
        content:
          "Apple intègre depuis iOS 11 un outil de diagnostic accessible dans Réglages → Batterie → État de la batterie. Vous y trouverez deux informations clés : la capacité maximale (en %) et le message sur la performance optimale.",
      },
      { type: "h3", content: "Capacité maximale en dessous de 80 %" },
      {
        type: "p",
        content:
          "Apple recommande officiellement le remplacement à partir de 79 %. En dessous, votre iPhone ne tient plus une journée complète et perd des fonctionnalités (limitation des performances en cas de pic).",
      },
      { type: "h3", content: "Performance bridée (\"limitation activée\")" },
      {
        type: "p",
        content:
          "Si vous voyez le message « Cet iPhone a déjà subi un arrêt inopiné parce que la batterie ne fournissait plus l'alimentation nécessaire », c'est qu'iOS bride volontairement le processeur pour éviter les redémarrages aléatoires. Changer la batterie restaure 100 % des performances.",
      },
      { type: "h3", content: "Signes physiques à surveiller" },
      {
        type: "ul",
        content: [
          "L'iPhone chauffe anormalement en charge ou en utilisation normale",
          "La batterie est physiquement gonflée (l'écran se décolle légèrement, vitre arrière qui bombe)",
          "Décharge brutale de 100 % à 30 % en quelques minutes",
          "Arrêts intempestifs même avec un % de batterie restant",
          "L'iPhone ne s'allume plus que branché",
        ],
      },
      {
        type: "note",
        content:
          "Une batterie gonflée est un signe d'URGENCE. Cessez d'utiliser votre iPhone et apportez-le immédiatement chez Mediastore Niort. Le gonflement peut endommager l'écran et la carte mère, voire présenter un risque d'incendie.",
      },
      { type: "h2", content: "Combien coûte le changement de batterie iPhone à Niort ?" },
      {
        type: "p",
        content:
          "Chez Mediastore Niort, nos tarifs sont parmi les plus compétitifs de la Deux-Sèvres. Tous nos remplacements incluent une batterie neuve haute capacité, l'intervention par un technicien certifié, le test post-réparation et la garantie 6 mois pièces et main-d'œuvre.",
      },
      {
        type: "ul",
        content: [
          "iPhone 15 / 15 Pro / Pro Max : à partir de 79 €",
          "iPhone 14 / 14 Pro / Pro Max : à partir de 69 €",
          "iPhone 13 / 13 Pro / Pro Max / mini : à partir de 59 €",
          "iPhone 12 / 12 Pro / Pro Max / mini : à partir de 55 €",
          "iPhone 11 / XR / XS / X : à partir de 49 €",
          "iPhone 8 / SE 2-3 : à partir de 45 €",
          "iPhone 7 / 6s / 6 : à partir de 39 €",
        ],
      },
      { type: "h2", content: "Combien de temps prend l'intervention ?" },
      {
        type: "p",
        content:
          "Le remplacement d'une batterie iPhone chez Mediastore Niort se fait en 30 à 60 minutes sur place dans notre atelier du 104 Rue de la Gare. Vous pouvez attendre en boutique, dans le centre-ville à proximité, ou revenir dans la journée. Sans rendez-vous, sauf samedi après-midi (très fréquenté).",
      },
      { type: "h2", content: "Pièces d'origine Apple ou compatibles premium ?" },
      {
        type: "p",
        content:
          "Mediastore Niort vous propose les deux options et vous laisse le choix. Les batteries d'origine Apple coûtent plus cher mais offrent une autonomie identique à l'iPhone d'usine. Les batteries compatibles premium (sourcées chez des fabricants certifiés) offrent un rapport qualité/prix optimal avec une autonomie équivalente à 95-100 % d'une batterie Apple. Dans tous les cas, garantie 6 mois.",
      },
      { type: "h2", content: "Puis-je changer ma batterie iPhone moi-même ?" },
      {
        type: "p",
        content:
          "Techniquement, c'est possible avec un kit iFixit ou équivalent. Mais nous le déconseillons fortement pour plusieurs raisons : risque de casser l'écran ou les nappes lors du démontage, batterie compatible difficile à sourcer en qualité fiable, perte d'étanchéité (joint à remplacer), et risque sur la santé en cas de manipulation incorrecte (batterie lithium). Pour une économie de 30-39 €, le risque ne vaut pas le coup. Confiez votre iPhone à un professionnel certifié.",
      },
      {
        type: "cta",
        content: {
          title: "Changer votre batterie iPhone à Niort dès aujourd'hui",
          text: "Diagnostic gratuit en boutique en 5 minutes. Intervention sur place en 30 minutes. Garantie 6 mois.",
          link: "/reparation-iphone-niort",
          linkText: "Voir tous nos tarifs iPhone",
        },
      },
    ],
  },

  {
    slug: "iphone-reconditionne-vs-neuf",
    title: "iPhone reconditionné ou neuf ? Comparatif complet 2026",
    description:
      "iPhone reconditionné chez Mediastore Niort vs neuf Apple : différences, qualité, garantie, économies. Tableau comparatif et conseils.",
    keywords:
      "iphone reconditionné niort, iphone reconditionné vs neuf, mediastore reconditionné, iphone occasion niort, économies iphone",
    date: "2026-02-05",
    author: "Mediastore",
    category: "Reconditionné",
    readTime: 5,
    image: DEFAULT_IMAGE,
    excerpt:
      "Économisez 25 à 50 % en optant pour un iPhone reconditionné Mediastore : 25 points de contrôle, garantie 12 mois, geste écologique. Comparatif détaillé.",
    sections: [
      {
        type: "p",
        content:
          "Acheter un iPhone neuf coûte cher : 1 000 € en moyenne pour un modèle Pro récent. L'iPhone reconditionné a longtemps souffert d'une image \"d'occasion bricolée\". Pourtant, un iPhone reconditionné par un professionnel comme Mediastore Niort offre quasi les mêmes garanties qu'un neuf, à un prix nettement inférieur. Ce comparatif vous aide à choisir en connaissance de cause.",
      },
      { type: "h2", content: "Qu'est-ce qu'un iPhone reconditionné, exactement ?" },
      {
        type: "p",
        content:
          "Un iPhone reconditionné est un iPhone d'occasion remis à neuf par un professionnel selon un protocole strict. Chez Mediastore Niort, chaque appareil passe par 25 points de contrôle : test batterie (santé minimum 85 %, sinon remplacement), test écran, test boutons, test connecteurs, tests réseau 4G/5G, test caméras avant/arrière, test capteur Face ID/Touch ID, test haut-parleurs et micros. Les pièces défaillantes sont systématiquement remplacées avant la mise en vente.",
      },
      { type: "h2", content: "iPhone reconditionné vs neuf : le comparatif" },
      { type: "h3", content: "Le prix" },
      {
        type: "p",
        content:
          "C'est la différence la plus visible. Un iPhone reconditionné Mediastore coûte 25 à 50 % moins cher qu'un neuf Apple à modèle équivalent. Exemple : iPhone 13 128 Go neuf Apple ≈ 729 €, iPhone 13 128 Go reconditionné Mediastore ≈ 449 €. Économie : 279 € pour un appareil quasi identique.",
      },
      { type: "h3", content: "La garantie" },
      {
        type: "p",
        content:
          "Apple offre 12 mois de garantie sur un iPhone neuf. Mediastore Niort offre exactement la même durée : 12 mois pièces et main-d'œuvre sur tous les iPhone reconditionnés. En cas de défaut, vous êtes pris en charge par notre atelier local, à 5 minutes au 104 Rue de la Gare. Pas de retour postal ni d'attente.",
      },
      { type: "h3", content: "L'esthétique" },
      {
        type: "p",
        content:
          "Mediastore Niort commercialise uniquement des grades A et A+. Grade A+ : aucune trace d'usure, comme neuf, batterie 95-100 %. Grade A : micro-rayures invisibles sans lumière directe, batterie 85-95 %. Aucun grade B (rayures visibles) ou C (chocs) chez nous. Visuellement, vos amis ne verront pas la différence.",
      },
      { type: "h3", content: "Les performances" },
      {
        type: "p",
        content:
          "Strictement identiques. Un iPhone reconditionné a le même processeur, la même mémoire, le même appareil photo, le même écran. Toutes ses fonctionnalités logicielles (Face ID, Apple Pay, iMessage, FaceTime, iCloud) fonctionnent à 100 %. La seule différence pourrait être l'autonomie si la batterie n'est pas neuve — sauf chez Mediastore Niort où nous la remplaçons systématiquement si elle est < 85 %.",
      },
      { type: "h3", content: "L'impact écologique" },
      {
        type: "p",
        content:
          "Fabriquer un iPhone neuf émet environ 70 kg de CO2 (extraction minerais, transport, assemblage). Prolonger la vie d'un iPhone existant permet d'éviter cette empreinte. Sur 3-4 ans, le bilan carbone d'un reconditionné est divisé par 5 à 10 vs un neuf.",
      },
      { type: "h2", content: "Tableau comparatif rapide" },
      {
        type: "ul",
        content: [
          "Prix : Mediastore reconditionné 25-50 % moins cher",
          "Garantie : 12 mois dans les 2 cas",
          "Esthétique : neuf parfait | reconditionné grade A/A+ quasi-neuf",
          "Performances : strictement identiques",
          "SAV : Apple (web/téléphone, France ou autre) | Mediastore (Niort, en personne)",
          "Empreinte carbone : reconditionné = 1/5 à 1/10 d'un neuf",
          "Essai avant achat : impossible (Apple) | possible (Mediastore boutique)",
        ],
      },
      { type: "h2", content: "Quand acheter neuf plutôt que reconditionné ?" },
      {
        type: "p",
        content:
          "Trois cas de figure justifient l'achat neuf : 1) Vous voulez ABSOLUMENT le tout dernier modèle de l'année dès sa sortie. 2) Votre employeur exige une facture d'achat neuf pour un IPhone professionnel financé. 3) Vous vous offrez un iPhone Pro comme cadeau personnel et la sensation \"déballer un neuf\" compte. Sinon, le reconditionné offre 80-90 % de l'expérience pour 50-75 % du prix.",
      },
      { type: "h2", content: "Acheter un iPhone reconditionné à Niort en pratique" },
      {
        type: "p",
        content:
          "Notre boutique de Niort vous permet d'essayer chaque iPhone reconditionné en stock avant l'achat : prise en main, test caméra, vitesse, autonomie. Vous repartez avec : iPhone propre désinfecté, câble USB neuf, certificat de reconditionnement, facture nominative, garantie 12 mois. Reprise possible de votre ancien iPhone pour réduire encore le prix.",
      },
      {
        type: "cta",
        content: {
          title: "Trouvez votre iPhone reconditionné à Niort",
          text: "Stock disponible en boutique. Essai sur place. Garantie 12 mois. Reprise possible.",
          link: "/iphone-reconditionne-niort",
          linkText: "Voir tous nos iPhone reconditionnés",
        },
      },
    ],
  },

  // ============================================================
  // Article 3 — Tarif écran iPhone
  // ============================================================
  {
    slug: "tarif-ecran-iphone-niort",
    title: "Combien coûte un écran iPhone à Niort ? Tarifs 2026",
    description: "Tarifs détaillés du changement d'écran iPhone à Niort par modèle. Original Apple ou compatible premium ? Délai, garantie, conseils Mediastore.",
    keywords: "écran iphone niort, prix écran iphone, remplacement écran iphone, tarif réparation iphone niort, écran cassé iphone",
    date: "2026-02-10",
    author: "Mediastore",
    category: "Réparation",
    readTime: 5,
    image: DEFAULT_IMAGE,
    excerpt: "L'écran de votre iPhone est cassé ? Voici le détail des tarifs par modèle chez Mediastore Niort, les délais et la différence entre pièce d'origine et compatible premium.",
    sections: [
      { type: "p", content: "L'écran cassé est de loin la première raison de réparation d'iPhone à Niort. Quel que soit le modèle (iPhone 7 à iPhone 16 Pro Max), Mediastore intervient sur place en 30 à 60 minutes. Voici les tarifs précis et tous les conseils pour bien choisir." },
      { type: "h2", content: "Tarifs écran iPhone à Niort - par modèle" },
      { type: "p", content: "Nos tarifs sont parmi les plus compétitifs de la Deux-Sèvres et incluent toujours la pièce, la pose, le test post-réparation et la garantie 6 mois pièces et main-d'œuvre." },
      { type: "ul", content: [
        "iPhone 15 / 15 Plus / 15 Pro / 15 Pro Max : à partir de 149 €",
        "iPhone 14 / 14 Plus / 14 Pro / 14 Pro Max : à partir de 129 €",
        "iPhone 13 / 13 mini / 13 Pro / 13 Pro Max : à partir de 109 €",
        "iPhone 12 / 12 mini / 12 Pro / 12 Pro Max : à partir de 89 €",
        "iPhone 11 / 11 Pro / 11 Pro Max / XR / XS / X : à partir de 79 €",
        "iPhone 8 / 8 Plus / SE 2-3 : à partir de 59 €",
        "iPhone 7 / 7 Plus / 6s / 6 : à partir de 49 €"
      ]},
      { type: "h2", content: "Pourquoi de telles écarts de prix entre modèles ?" },
      { type: "p", content: "Le tarif dépend de trois facteurs : le coût de la pièce (un écran OLED ProMotion 120 Hz coûte 3 à 5 fois plus cher qu'une dalle LCD), la complexité du démontage (les modèles récents intègrent Face ID, True Tone, et de nombreuses nappes fragiles), et la disponibilité de la pièce sur le marché (les modèles très récents ont un stock pièces limité)." },
      { type: "h2", content: "Original Apple vs compatible premium : que choisir ?" },
      { type: "h3", content: "Écran d'origine Apple" },
      { type: "p", content: "Garantit 100 % les fonctionnalités True Tone, ProMotion 120 Hz, calibration colorimétrique d'usine. Plus cher d'environ 30 à 40 %. Recommandé pour les iPhone Pro récents et les utilisateurs sensibles à la qualité d'affichage." },
      { type: "h3", content: "Écran compatible premium" },
      { type: "p", content: "Sourcé chez des fabricants certifiés. Qualité d'affichage à 95 % de l'original. True Tone compatible (auto-calibration). Rapport qualité/prix optimal pour les iPhone non Pro et les budgets serrés. Garantie identique 6 mois chez Mediastore Niort." },
      { type: "h2", content: "Combien de temps pour remplacer un écran iPhone ?" },
      { type: "p", content: "Sur place dans notre atelier de Niort, le remplacement d'écran se fait en 30 à 60 minutes pour la majorité des modèles. Vous pouvez attendre en boutique ou repasser dans la journée. Pour les modèles très récents nécessitant une calibration spécifique (iPhone 15 Pro Max), comptez 1 h à 1 h 30." },
      { type: "h2", content: "Mon écran iPhone n'est que fissuré, dois-je vraiment le changer ?" },
      { type: "p", content: "Une fissure superficielle ne nécessite pas forcément un remplacement immédiat. Mais attention : les fissures laissent passer la poussière et l'humidité qui peuvent à terme endommager la dalle LCD ou les capteurs (Face ID, True Tone). Si vous continuez à utiliser un iPhone fissuré, prévoyez un remplacement sous 2-3 mois pour éviter une panne plus coûteuse." },
      { type: "note", content: "ATTENTION : un écran d'iPhone fissuré peut couper la peau (microparticules de verre). Si vous voyez du verre s'écailler, apportez votre iPhone chez Mediastore Niort dans les plus brefs délais." },
      { type: "h2", content: "Diagnostic et devis gratuits" },
      { type: "p", content: "Que vous soyez de Niort, Chauray, Aiffres, Bessines, Magné ou ailleurs en Deux-Sèvres, nous vous accueillons sans rendez-vous. Le diagnostic est gratuit, le devis transparent, et vous repartez en connaissant tous les tarifs avant la moindre intervention." },
      { type: "cta", content: { title: "Réparez votre écran iPhone à Niort", text: "Diagnostic gratuit. Intervention en 30 minutes. Garantie 6 mois.", link: "/reparation-iphone-niort", linkText: "Voir tous nos services iPhone" } },
    ],
  },

  // ============================================================
  // Article 4 — Dégât des eaux
  // ============================================================
  {
    slug: "iphone-tombe-eau-que-faire",
    title: "iPhone ou Mac tombé dans l'eau : que faire en urgence ?",
    description: "iPhone ou MacBook tombé dans l'eau ? Gestes d'urgence à connaître, ce qu'il ne faut SURTOUT pas faire, et nos tarifs désoxydation à Niort.",
    keywords: "iphone eau niort, désoxydation iphone niort, macbook eau, mediastore désoxydation, iphone dégât eau",
    date: "2026-02-13",
    author: "Mediastore",
    category: "Conseils",
    readTime: 5,
    image: DEFAULT_IMAGE,
    excerpt: "Votre iPhone est tombé dans l'eau, dans la cuvette, dans la mer ou sous la douche ? Voici les gestes qui peuvent le sauver, et ceux qui le condamnent.",
    sections: [
      { type: "p", content: "Chaque année, Mediastore Niort traite des dizaines de cas de dégâts des eaux sur iPhone, iPad et MacBook. Avec les bons gestes dans les minutes qui suivent l'incident, on récupère 70 à 90 % des appareils. Avec les MAUVAIS gestes, on les détruit définitivement. Ce guide pratique vous explique tout." },
      { type: "h2", content: "ÉTAPE 1 : éteindre IMMÉDIATEMENT l'appareil" },
      { type: "p", content: "Le pire ennemi d'un appareil mouillé n'est pas l'eau elle-même, mais le COURANT ÉLECTRIQUE qui circule pendant que l'eau est encore dans les circuits. Si votre iPhone est allumé, l'eau crée des courts-circuits qui détruisent les composants en quelques secondes." },
      { type: "ul", content: [
        "Appuyez longuement sur le bouton latéral + un bouton volume pour éteindre",
        "Si l'écran ne répond plus, forcez l'arrêt : pression rapide volume+, puis volume-, puis maintenez le bouton latéral 10 secondes",
        "Ne tentez SURTOUT PAS de l'allumer pour vérifier si ça marche",
        "Ne le branchez PAS au chargeur (vous créeriez un mega court-circuit)"
      ]},
      { type: "h2", content: "ÉTAPE 2 : essuyer extérieurement et ne pas secouer" },
      { type: "p", content: "Avec un chiffon propre et sec, essuyez l'extérieur. Tournez l'appareil dans tous les sens DÉLICATEMENT pour évacuer l'eau par les ouvertures (haut-parleur, micro, port de charge). NE LE SECOUEZ JAMAIS : vous propagez l'eau dans la carte mère." },
      { type: "h2", content: "ÉTAPE 3 : NE JAMAIS faire ces erreurs" },
      { type: "ul", content: [
        "❌ Ne mettez PAS dans un sac de riz : c'est un mythe, le riz fait plus de mal que de bien (poussière dans les ports)",
        "❌ N'utilisez PAS un sèche-cheveux : la chaleur déforme les composants et pousse l'eau plus profondément",
        "❌ Ne le mettez PAS au four ou au radiateur : même raison",
        "❌ N'essayez PAS de l'ouvrir vous-même : vous perdez l'étanchéité résiduelle",
        "❌ N'attendez PAS 'pour voir si ça sèche tout seul' : l'oxydation commence en 30 minutes"
      ]},
      { type: "h2", content: "ÉTAPE 4 : apporter IMMÉDIATEMENT chez Mediastore Niort" },
      { type: "p", content: "Le temps est CRITIQUE. Plus vous attendez, plus l'oxydation détruit les composants. Idéalement, apportez votre appareil chez Mediastore Niort dans les 2 heures suivant l'incident. Notre atelier dispose d'équipement de désoxydation sous microscope : nous démontons entièrement l'appareil, nettoyons la carte mère aux ultrasons et à l'isopropanol 99 %, puis remontons et testons." },
      { type: "h2", content: "Tarifs désoxydation à Niort" },
      { type: "ul", content: [
        "Désoxydation iPhone : à partir de 59 €",
        "Désoxydation iPad : à partir de 79 €",
        "Désoxydation MacBook : à partir de 99 €",
        "Sur devis pour les cas complexes nécessitant micro-soudure"
      ]},
      { type: "h2", content: "Taux de récupération chez Mediastore Niort" },
      { type: "p", content: "Notre taux de succès dépend essentiellement du DÉLAI entre la chute et l'intervention. Apporté dans les 2 heures : 85-90 % de récupération. Dans la journée : 70-80 %. Après 48 h : 30-50 %. Au-delà d'une semaine : souvent trop tard, l'oxydation a détruit la carte mère." },
      { type: "h2", content: "Cas particuliers : eau salée et eau sucrée" },
      { type: "p", content: "L'eau de mer (sel) et les boissons sucrées (soda, jus) sont BEAUCOUP plus destructrices que l'eau claire. Le sel et le sucre cristallisent en séchant et créent des ponts conducteurs permanents. Pour ces cas, intervention urgente sous 1 heure si possible." },
      { type: "cta", content: { title: "Urgence dégât des eaux à Niort", text: "Apportez votre appareil immédiatement éteint au 104 Rue de la Gare. Désoxydation dès 59 €.", link: "/urgence", linkText: "Page Urgence Réparation" } },
    ],
  },

  // ============================================================
  // Article 5 — Mac trop lent
  // ============================================================
  {
    slug: "mac-trop-lent-solutions",
    title: "Mac trop lent ? 7 solutions pour l'accélérer (sans le changer)",
    description: "Votre MacBook ou iMac rame ? 7 solutions pour redonner vie à votre Mac sans en acheter un neuf. Conseils gratuits Mediastore Niort.",
    keywords: "mac lent niort, accélérer macbook, mac rame, optimiser mac, mediastore mac niort",
    date: "2026-02-17",
    author: "Mediastore",
    category: "Conseils",
    readTime: 6,
    image: DEFAULT_IMAGE,
    excerpt: "Votre Mac mettait 30 secondes à démarrer il y a 3 ans, et maintenant c'est 5 minutes ? 7 solutions concrètes pour le rendre à nouveau rapide.",
    sections: [
      { type: "p", content: "Avant de penser à acheter un nouveau Mac à 1 499 €, sachez qu'avec 80 à 199 € d'optimisation, on peut souvent ressusciter un Mac de 5 à 8 ans et lui donner une seconde jeunesse. Voici 7 solutions classées de la plus simple à la plus technique." },
      { type: "h2", content: "1. Libérer de l'espace disque" },
      { type: "p", content: "macOS a besoin d'au moins 15 % d'espace libre pour fonctionner correctement. En dessous, le système rame fortement. Allez dans Réglages système → Général → Stockage et libérez de la place : cache, anciennes sauvegardes Time Machine locales, téléchargements oubliés, mails en pièce jointe." },
      { type: "h2", content: "2. Vider les caches et redémarrer" },
      { type: "p", content: "Un Mac qui n'a pas redémarré depuis des semaines accumule des caches mémoire qui ralentissent tout. Redémarrez complètement (Menu Pomme → Redémarrer). Pour aller plus loin : maintenir Shift au démarrage = mode sans extensions, qui vide les caches système." },
      { type: "h2", content: "3. Supprimer les applis au démarrage inutiles" },
      { type: "p", content: "Réglages système → Général → Ouverture. Désactivez tout ce qui n'est pas indispensable au démarrage (Dropbox, Spotify, anti-virus tiers, Microsoft AutoUpdate…). Vous gagnerez 30 % de vitesse au boot." },
      { type: "h2", content: "4. Passer du disque dur (HDD) au SSD" },
      { type: "p", content: "Si votre Mac date d'avant 2018 et a encore un disque dur mécanique (HDD), c'est LA modification qui change tout. Un SSD multiplie la vitesse d'accès par 10. Un MacBook Pro 2014 avec SSD redémarre en 12 secondes au lieu de 90." },
      { type: "p", content: "Mediastore Niort propose l'upgrade SSD à partir de 169 € (pièce + pose + clonage de votre système). Compatible MacBook Pro/Air 2009-2017, iMac jusqu'à 2017, Mac mini." },
      { type: "h2", content: "5. Ajouter de la mémoire RAM" },
      { type: "p", content: "Sur Mac Intel jusqu'à 2017, la RAM est upgradable. Passer de 4 ou 8 Go à 16 Go fait une différence MAJEURE pour le multitâche, les onglets Safari, la vidéo. Tarif Mediastore Niort : à partir de 49 € (mémoire 8 Go) à 149 € (mémoire 32 Go), pose incluse." },
      { type: "note", content: "Attention : sur les Mac Apple Silicon (M1, M2, M3, M4 — à partir de 2020), la mémoire est SOUDÉE à la puce. Impossible de l'upgrader après achat. Choisissez bien vos Go au moment de l'achat." },
      { type: "h2", content: "6. Désactiver les effets visuels" },
      { type: "p", content: "macOS utilise beaucoup d'effets graphiques (transparence, animations). Sur un Mac ancien, ils consomment des ressources inutilement. Allez dans Réglages → Accessibilité → Affichage et activez « Réduire la transparence » + « Réduire les animations »." },
      { type: "h2", content: "7. Réinstaller macOS proprement" },
      { type: "p", content: "Après 3-5 ans d'utilisation, le système macOS accumule des résidus de logiciels désinstallés, des permissions cassées, des paramètres corrompus. Une réinstallation propre depuis le mode Recovery (Cmd+R au démarrage) règle 80 % des lenteurs résiduelles." },
      { type: "p", content: "Mediastore Niort propose la réinstallation complète + transfert de vos données pour 79 €. Sauvegarde incluse, vous récupérez un Mac comme neuf." },
      { type: "h2", content: "Quand faut-il vraiment changer de Mac ?" },
      { type: "p", content: "Si votre Mac a plus de 8 ans, ne supporte plus la dernière version de macOS, et que les solutions ci-dessus ne suffisent pas, c'est le moment. Mais sachez que la plupart des Mac de 2017 peuvent encore très bien tourner sous macOS Sequoia avec un SSD + 16 Go RAM, pour 199 € d'optimisation." },
      { type: "cta", content: { title: "Accélérez votre Mac à Niort", text: "Diagnostic gratuit, devis transparent. Upgrade SSD/RAM, nettoyage système.", link: "/reparation-mac-niort", linkText: "Voir tous nos services Mac" } },
    ],
  },

  // ============================================================
  // Article 6 — Comparatif iPhone 14/15/16
  // ============================================================
  {
    slug: "iphone-14-15-16-comparatif",
    title: "iPhone 14, 15 ou 16 : lequel choisir en 2026 ?",
    description: "Comparatif iPhone 14, 15 et 16 : différences, prix, autonomie, photo, USB-C, Dynamic Island. Le guide d'achat Mediastore Niort.",
    keywords: "iphone 14 15 16 niort, choisir iphone niort, comparatif iphone, iphone reconditionné niort, iphone neuf occasion",
    date: "2026-02-20",
    author: "Mediastore",
    category: "Conseils",
    readTime: 7,
    image: DEFAULT_IMAGE,
    excerpt: "Vous hésitez entre iPhone 14, 15 ou 16 ? Voici le comparatif honnête de nos experts Mediastore : différences techniques, vrais usages, prix neuf vs reconditionné.",
    sections: [
      { type: "p", content: "Chaque année, Apple sort un nouvel iPhone et le marketing nous fait croire que c'est révolutionnaire. La réalité ? Les écarts entre 2 générations consécutives sont souvent mineurs, mais entre 3 générations, les différences deviennent significatives. Voici notre comparatif honnête, basé sur des centaines d'iPhone passés entre nos mains dans notre atelier de Niort." },
      { type: "h2", content: "iPhone 14 : le bon plan rapport qualité/prix" },
      { type: "p", content: "Sorti fin 2022. Toujours commercialisé neuf par Apple en 2026. Disponible largement en reconditionné chez Mediastore Niort à partir de 599 €." },
      { type: "ul", content: [
        "✅ Écran Super Retina XDR 6,1\" 60 Hz",
        "✅ Puce A15 Bionic (suffisante pour 95 % des usages 4-5 ans)",
        "✅ Double caméra 12 Mpx avec amélioration faible luminosité",
        "✅ Autonomie correcte (1 journée intensive)",
        "❌ Pas de Dynamic Island (encoche classique)",
        "❌ Connecteur Lightning (plus USB-C)",
        "❌ Pas de ProMotion 120 Hz"
      ]},
      { type: "h2", content: "iPhone 15 : la transition USB-C" },
      { type: "p", content: "Sorti fin 2023. Apporte des changements majeurs vs iPhone 14, notamment le passage à l'USB-C imposé par l'Europe. Reconditionné Mediastore à partir de 699 €." },
      { type: "ul", content: [
        "✅ Connecteur USB-C (un seul câble pour tous vos appareils)",
        "✅ Dynamic Island (la barre interactive en haut)",
        "✅ Caméra principale 48 Mpx avec zoom 2x",
        "✅ Châssis aluminium / verre coloré agréable au toucher",
        "✅ Puce A16 (gain perf vs A15 modéré)",
        "❌ Écran toujours 60 Hz (sauf version Pro)",
        "❌ Pas d'Apple Intelligence"
      ]},
      { type: "h2", content: "iPhone 16 : Apple Intelligence et performances" },
      { type: "p", content: "Sorti fin 2024. Premier iPhone compatible Apple Intelligence (IA générative Apple). Reconditionné Mediastore à partir de 899 €." },
      { type: "ul", content: [
        "✅ Apple Intelligence (résumés mails, retouche photo IA, Siri amélioré)",
        "✅ Bouton Action personnalisable + nouveau Camera Control",
        "✅ Puce A18 (très en avance sur A15-A16)",
        "✅ Photos sublimées par traitement IA",
        "✅ Autonomie excellente",
        "❌ Prix élevé en neuf (969 €)",
        "❌ Apple Intelligence pas encore mature en français"
      ]},
      { type: "h2", content: "Notre recommandation par profil utilisateur" },
      { type: "h3", content: "Vous changez tous les 5-6 ans, budget serré" },
      { type: "p", content: "iPhone 14 reconditionné Mediastore Niort. Vous économisez 399 € vs iPhone 16 neuf, vous gardez l'appareil 5 ans, et vous avez 95 % des fonctionnalités." },
      { type: "h3", content: "Vous changez tous les 3-4 ans, usage avancé" },
      { type: "p", content: "iPhone 15 reconditionné ou iPhone 15 Pro reconditionné. USB-C, Dynamic Island, performances solides pour 4 ans à venir." },
      { type: "h3", content: "Vous voulez le top, vous aimez l'IA" },
      { type: "p", content: "iPhone 16 ou 16 Pro reconditionné. Apple Intelligence va devenir indispensable d'ici 2 ans, autant en profiter dès maintenant." },
      { type: "h2", content: "Reconditionné ou neuf ?" },
      { type: "p", content: "Sauf si vous voulez ABSOLUMENT l'iPhone à sa sortie ou un cadeau emballé neuf, le reconditionné Mediastore offre 95 % de l'expérience pour 25 à 50 % de moins. Garantie 12 mois identique, grades A/A+ uniquement chez nous." },
      { type: "cta", content: { title: "Découvrez nos iPhone reconditionnés à Niort", text: "Stock disponible. Essai en boutique. Garantie 12 mois. Reprise possible.", link: "/iphone-reconditionne-niort", linkText: "Voir le stock iPhone reconditionnés" } },
    ],
  },

  // ============================================================
  // Article 7 — Sauvegarder données avant réparation
  // ============================================================
  {
    slug: "sauvegarder-donnees-avant-reparation",
    title: "Sauvegarder ses données avant une réparation : guide pratique",
    description: "Comment sauvegarder iPhone, iPad, Mac et PC avant une réparation. Méthodes iCloud, Google Drive, disque externe. Conseils Mediastore Niort.",
    keywords: "sauvegarde iphone niort, sauvegarde mac niort, icloud sauvegarde, time machine, sauvegarde avant réparation",
    date: "2026-02-24",
    author: "Mediastore",
    category: "Conseils",
    readTime: 5,
    image: DEFAULT_IMAGE,
    excerpt: "Avant d'apporter votre iPhone ou Mac en réparation, sauvegardez TOUT. Voici les méthodes à utiliser, étape par étape, pour ne rien perdre.",
    sections: [
      { type: "p", content: "Une réparation peut TRÈS bien se passer sans perte de données. Mais une réparation peut aussi mal tourner : carte mère HS, écran qui révèle un problème logiciel, batterie qui se gonfle pendant l'opération. La règle d'or : SAUVEGARDEZ TOUJOURS avant. Voici comment, simplement." },
      { type: "h2", content: "iPhone & iPad : 2 méthodes" },
      { type: "h3", content: "Méthode 1 : iCloud (la plus simple)" },
      { type: "ol", content: [
        "Réglages → Votre nom → iCloud → Sauvegarde iCloud",
        "Activez « Sauvegarder cet iPhone »",
        "Appuyez sur « Sauvegarder maintenant »",
        "Attendez que la sauvegarde se termine (5 à 60 min selon volume)",
        "Vérifiez l'heure « Dernière sauvegarde réussie »"
      ]},
      { type: "p", content: "iCloud offre 5 Go gratuits, souvent insuffisants. Souscrivez iCloud+ 50 Go à 0,99 €/mois pour 1 mois si besoin. Vous pourrez résilier après." },
      { type: "h3", content: "Méthode 2 : Mac/PC (gratuite et complète)" },
      { type: "ol", content: [
        "Connectez l'iPhone à votre Mac/PC en USB",
        "Ouvrez Finder (Mac) ou iTunes/Devices (PC)",
        "Cliquez sur l'iPhone dans la barre latérale",
        "Cochez « Sauvegarder sur cet ordinateur »",
        "Cochez « Chiffrer la sauvegarde locale » (recommandé)",
        "Cliquez « Sauvegarder maintenant »"
      ]},
      { type: "h2", content: "Mac : Time Machine" },
      { type: "p", content: "Time Machine est l'outil de sauvegarde intégré à macOS, simple et automatique." },
      { type: "ol", content: [
        "Branchez un disque externe USB ou Thunderbolt (1 To minimum)",
        "macOS vous propose de l'utiliser pour Time Machine — acceptez",
        "Réglages système → Time Machine",
        "Première sauvegarde : 30 min à 4 h selon volume",
        "Conservez le disque chez vous, NE l'apportez PAS avec votre Mac en réparation"
      ]},
      { type: "h2", content: "PC Windows : sauvegarde Windows + cloud" },
      { type: "ol", content: [
        "Sauvegarde Windows : Paramètres → Mise à jour et sécurité → Sauvegarde → Ajouter un lecteur",
        "Choisissez un disque externe",
        "Activez « Sauvegarder automatiquement mes fichiers »",
        "En parallèle, OneDrive ou Google Drive pour vos documents importants"
      ]},
      { type: "h2", content: "Documents pro et photos : 2 copies minimum" },
      { type: "p", content: "Pour les documents critiques (entreprise, comptabilité, photos famille irremplaçables), suivez la règle 3-2-1 : 3 copies des données, sur 2 supports différents, dont 1 hors site (cloud)." },
      { type: "h2", content: "Et si je ne peux pas allumer mon iPhone ou Mac ?" },
      { type: "p", content: "Si l'appareil ne s'allume plus du tout, la sauvegarde directe n'est pas possible. Apportez-le tel quel chez Mediastore Niort : nous proposons un service de récupération de données AVANT réparation, à partir de 79 €. Taux de récupération élevé sauf en cas de panne mécanique sévère (disque dur cassé physiquement)." },
      { type: "cta", content: { title: "Réparation et récupération de données à Niort", text: "Service de récupération préventive disponible. Diagnostic gratuit.", link: "/contact", linkText: "Nous contacter" } },
    ],
  },

  // ============================================================
  // Article 8 — Garantie 6 mois
  // ============================================================
  {
    slug: "garantie-6-mois-mediastore",
    title: "Garantie 6 mois Mediastore : ce qu'elle couvre exactement",
    description: "Tout savoir sur la garantie 6 mois Mediastore Niort : pièces couvertes, exclusions, procédure de SAV. Engagement qualité et transparence.",
    keywords: "garantie réparation niort, garantie iphone niort, sav mediastore, garantie 6 mois apple",
    date: "2026-02-27",
    author: "Mediastore",
    category: "Mediastore",
    readTime: 4,
    image: DEFAULT_IMAGE,
    excerpt: "Toutes nos réparations à Niort sont garanties 6 mois pièces et main-d'œuvre. Voici exactement ce que ça couvre, et les conditions à connaître.",
    sections: [
      { type: "p", content: "La garantie 6 mois Mediastore Niort est notre engagement qualité depuis plus de 10 ans. Ni promesse marketing ni petits caractères : voici en clair tout ce qu'elle couvre, et les rares cas où elle ne s'applique pas." },
      { type: "h2", content: "Ce que la garantie COUVRE" },
      { type: "ul", content: [
        "Toute défaillance de la PIÈCE remplacée (écran qui devient noir, batterie qui ne tient plus, connecteur qui se déconnecte)",
        "Toute erreur de MAIN-D'ŒUVRE (vis manquante, joint mal posé, nappe mal connectée)",
        "Reprise GRATUITE de l'intervention chez Mediastore Niort en cas de défaut",
        "Conseils SAV téléphoniques gratuits à tout moment durant les 6 mois",
        "Couverture VALIDE même si vous n'êtes pas l'acheteur initial (utile pour les iPhone reçus en cadeau)"
      ]},
      { type: "h2", content: "Ce que la garantie NE couvre PAS" },
      { type: "ul", content: [
        "Une NOUVELLE chute ou choc après la réparation (l'écran que nous avons posé re-casse parce que vous avez fait tomber votre iPhone)",
        "Un dégât LIQUIDE survenu après notre intervention",
        "Une PANNE différente de celle réparée (vous nous apportez pour un écran, 4 mois plus tard la carte mère lâche : c'est un cas différent)",
        "Les MODIFICATIONS logicielles non standard (jailbreak iPhone, hackintosh, ROM custom Android)",
        "L'usure normale d'une batterie remplacée (perte progressive de capacité au-delà de 80 %)"
      ]},
      { type: "h2", content: "Comment activer la garantie ?" },
      { type: "ol", content: [
        "Conservez votre facture (envoyée par mail + papier en boutique)",
        "En cas de défaut, appelez le 05 49 05 74 90",
        "Décrivez le problème et la date de la réparation initiale",
        "Apportez l'appareil à notre atelier 104 Rue de la Gare",
        "Diagnostic gratuit, prise en charge sous garantie si le défaut est avéré",
        "Reprise sous 24 à 48 h selon la nature de la pièce"
      ]},
      { type: "h2", content: "Durée 6 mois : pourquoi pas 12 ?" },
      { type: "p", content: "La durée légale de garantie en France pour les pièces de réparation est de 6 mois. La plupart de nos concurrents proposent 3 mois, certains rien du tout. Notre 6 mois est DEUX FOIS plus long que la moyenne du marché. Pour les iPhone reconditionnés vendus en boutique, la garantie passe à 12 mois (conformément au statut produit-vente)." },
      { type: "h2", content: "Pièces premium pour mieux durer" },
      { type: "p", content: "Nous utilisons exclusivement des pièces premium (Apple d'origine ou compatibles certifiées). Pas de pièces low-cost no-name, pas de pièces récupérées sur des téléphones cassés. C'est ce qui nous permet de maintenir une garantie 6 mois avec un taux de retour SAV inférieur à 3 %." },
      { type: "cta", content: { title: "Réparation garantie 6 mois à Niort", text: "Toutes nos interventions, sur toutes les marques, garanties 6 mois. Diagnostic gratuit.", link: "/garantie", linkText: "Voir notre page Garantie complète" } },
    ],
  },

  // ============================================================
  // Article 9 — Maintenance informatique TPE/PME
  // ============================================================
  {
    slug: "maintenance-informatique-entreprise-niort",
    title: "Maintenance informatique TPE/PME à Niort : pourquoi externaliser ?",
    description: "Pourquoi externaliser la maintenance informatique de votre TPE/PME à Niort ? Avantages, coûts, services Mediastore. Contrats à partir de 49 €/mois.",
    keywords: "maintenance informatique niort, infogérance niort, contrat maintenance entreprise, support informatique tpe pme niort",
    date: "2026-03-03",
    author: "Mediastore",
    category: "Pro",
    readTime: 6,
    image: DEFAULT_IMAGE,
    excerpt: "TPE ou PME à Niort, vous gérez votre informatique en interne ? Voici pourquoi externaliser auprès de Mediastore peut vous faire économiser 60 % et dormir tranquille.",
    sections: [
      { type: "p", content: "Les TPE et PME de Niort et Deux-Sèvres sont souvent confrontées au même dilemme : recruter un informaticien en interne (cher, mais réactif) ou externaliser auprès d'un prestataire (économique mais à risque). Avec un contrat de maintenance informatique Mediastore Niort, vous bénéficiez du meilleur des deux mondes pour 49 € HT/poste/mois en moyenne." },
      { type: "h2", content: "Pourquoi externaliser plutôt qu'embaucher ?" },
      { type: "p", content: "Un informaticien en CDI coûte en moyenne 38 000 € HT/an chargé (salaire + charges) pour une TPE qui n'a pas besoin de lui à plein temps. Avec un contrat Mediastore Niort, vous payez uniquement les services consommés, vous bénéficiez de l'expertise de plusieurs techniciens spécialisés (réseau, sécurité, Mac, PC), et vous évitez les périodes de congés sans support." },
      { type: "h2", content: "Que comprend un contrat Mediastore Niort ?" },
      { type: "ul", content: [
        "Monitoring 24/7 de vos serveurs et postes (alerte automatique en cas de défaillance)",
        "Mises à jour Windows / macOS / antivirus pilotées à distance",
        "Sauvegardes automatiques quotidiennes (locales NAS + cloud chiffré)",
        "Hotline prioritaire (intervention à distance en 30 min)",
        "1 intervention sur site / mois incluse",
        "Tarif préférentiel sur les pièces et matériels",
        "Audit sécurité annuel"
      ]},
      { type: "h2", content: "Tarification par taille d'entreprise" },
      { type: "ul", content: [
        "TPE 1-5 postes : forfait global à partir de 199 € HT/mois (tout compris)",
        "PME 5-20 postes : 49 € HT/poste/mois avec dégressivité",
        "PME 20+ postes : tarif négocié, devis personnalisé",
        "Sans contrat : intervention au coup par coup à 75 € HT/h sur site Niort"
      ]},
      { type: "h2", content: "Quand l'externalisation devient indispensable ?" },
      { type: "ol", content: [
        "Vous gérez des données sensibles (clients, médical, comptable) : sécurité = priorité",
        "Vous travaillez avec Microsoft 365, Office Teams ou Google Workspace",
        "Vous avez plus de 5 postes informatiques",
        "Une panne d'1 heure vous coûte plus que votre contrat mensuel (calculez votre seuil de rentabilité)",
        "Vous êtes soumis au RGPD et craignez une cyberattaque"
      ]},
      { type: "h2", content: "Mediastore Niort : un partenaire local de confiance" },
      { type: "p", content: "Contrairement à des prestataires distants ou des plateformes en ligne, Mediastore est physiquement à Niort au 104 Rue de la Gare. En cas de problème majeur, vous pouvez compter sur une intervention sur site sous 4 h ouvrées. Nos techniciens connaissent vos locaux, votre équipe, vos contraintes métier. La relation se construit dans la durée." },
      { type: "h2", content: "Cas d'usage : sécurité et RGPD" },
      { type: "p", content: "Depuis 2024, les cyberattaques sur les TPE/PME ont augmenté de 300 %. Le risque numéro 1 est le ransomware (chiffrement de toutes vos données contre rançon). Un contrat Mediastore inclut des sauvegardes hors-ligne quotidiennes, c'est votre seul vrai filet de sécurité." },
      { type: "cta", content: { title: "Demandez votre audit informatique gratuit à Niort", text: "Notre équipe étudie vos besoins et vous propose un contrat sur-mesure.", link: "/services-professionnels", linkText: "Voir tous nos services Pro" } },
    ],
  },

  // ============================================================
  // Article 10 — Vendre son iPhone
  // ============================================================
  {
    slug: "vendre-iphone-niort-reprise-depot-vente",
    title: "Vendre son iPhone à Niort : reprise sur achat vs dépôt-vente",
    description: "Comment vendre son iPhone à Niort ? Comparatif reprise sur achat (rapide) vs dépôt-vente (rendement) chez Mediastore. Estimation gratuite.",
    keywords: "vendre iphone niort, reprise iphone niort, dépôt vente iphone niort, mediastore reprise, estimation iphone occasion",
    date: "2026-03-06",
    author: "Mediastore",
    category: "Reconditionné",
    readTime: 5,
    image: DEFAULT_IMAGE,
    excerpt: "Vous voulez vendre votre iPhone à Niort ? Chez Mediastore, deux solutions : reprise sur achat ou dépôt-vente. Comparatif, valorisation, étapes pratiques.",
    sections: [
      { type: "p", content: "Vous changez d'iPhone et l'ancien dort dans un tiroir ? Mauvaise idée : un iPhone qui dort perd 10 à 20 % de sa valeur par an. Mediastore Niort vous propose deux formules pour le valoriser intelligemment : la REPRISE sur achat (rapide) ou le DÉPÔT-VENTE (rendement maximum). Voici les différences concrètes pour vous aider à choisir." },
      { type: "h2", content: "Reprise sur achat : la solution rapide" },
      { type: "p", content: "Principe : vous apportez votre iPhone, nous l'évaluons en 5 minutes, vous repartez avec un bon d'achat utilisable IMMÉDIATEMENT sur un nouvel appareil ou un service Mediastore." },
      { type: "ul", content: [
        "✅ Procédure rapide : 15 minutes en boutique",
        "✅ Argent immédiat sous forme de crédit",
        "✅ Aucun risque, pas d'attente",
        "✅ Couvre aussi les iPhone cassés ou HS (valorisation réduite mais possible)",
        "❌ Montant moins élevé que la vente directe (commission Mediastore + risque assumé)"
      ]},
      { type: "h2", content: "Dépôt-vente : la solution rendement" },
      { type: "p", content: "Principe : vous confiez votre iPhone à Mediastore Niort. Nous nous chargeons de la vente à notre clientèle locale (qui privilégie les iPhone vérifiés chez un professionnel). Quand l'iPhone est vendu, vous récupérez le produit de la vente moins une commission." },
      { type: "ul", content: [
        "✅ Montant nettement plus élevé que la reprise",
        "✅ Mediastore gère TOUT (annonce, photos, communication, paiement, garantie acheteur)",
        "✅ Aucun déplacement, aucune négociation à gérer",
        "✅ Mandat de vente écrit, traçabilité totale",
        "❌ Délai : 2 à 4 semaines en moyenne (1 semaine pour les modèles très recherchés)",
        "❌ Reste la possibilité, faible, que l'iPhone ne se vende pas"
      ]},
      { type: "h2", content: "Comment Mediastore évalue votre iPhone ?" },
      { type: "p", content: "Notre évaluation est gratuite et transparente, basée sur 4 critères principaux :" },
      { type: "ol", content: [
        "Le MODÈLE et l'année de sortie (un iPhone 13 vaut plus qu'un iPhone X)",
        "La CAPACITÉ de stockage (256 Go > 128 Go > 64 Go)",
        "L'ÉTAT GÉNÉRAL (écran, châssis, vitre arrière, boutons)",
        "L'ÉTAT DE LA BATTERIE (% santé via Réglages → Batterie)"
      ]},
      { type: "p", content: "Vous repartez avec une offre écrite, valable 7 jours, sans engagement de votre part. Vous pouvez comparer avec d'autres rachats en ligne et revenir en toute liberté." },
      { type: "h2", content: "Que faire si mon iPhone est cassé ou HS ?" },
      { type: "p", content: "Bonne nouvelle : Mediastore est aussi spécialiste réparation. Si votre iPhone a un écran cassé, une batterie morte ou autre, nous chiffrons à part la remise en état et l'intégrons à la valeur de reprise. Vous repartez avec un montant valorisé, sans avoir à faire la réparation vous-même." },
      { type: "h2", content: "Effacement de vos données : c'est nous qui le faisons" },
      { type: "p", content: "Beaucoup de propriétaires hésitent à vendre par peur de mal effacer leurs données. Chez Mediastore Niort, nous effectuons GRATUITEMENT la déconnexion iCloud + réinitialisation complète devant vous. Vos photos, messages, identifiants disparaissent de manière irréversible. Vous repartez l'esprit tranquille." },
      { type: "h2", content: "Pourquoi Mediastore plutôt que Leboncoin / Recommerce ?" },
      { type: "ul", content: [
        "Vous évitez les arnaques, les acheteurs qui annulent, les négociations chronophages",
        "Mediastore garantit l'iPhone à l'acheteur final → vous êtes dégagé de toute responsabilité après vente",
        "Vous évitez les frais Vinted/Recommerce (15-20 % de commission cachée)",
        "Vous soutenez un commerce local de Niort plutôt qu'une plateforme distante",
        "Vous repartez avec une facture, traçabilité totale"
      ]},
      { type: "cta", content: { title: "Estimez votre iPhone gratuitement à Niort", text: "5 minutes en boutique, offre écrite valable 7 jours, sans engagement.", link: "/reprise-iphone-niort", linkText: "Voir les détails Reprise & Dépôt-vente" } },
    ],
  },
];

export const getArticleBySlug = (slug) =>
  BLOG_ARTICLES.find((a) => a.slug === slug) || null;
